import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';

const containerWidthMap = { narrow: 800, default: 1200, wide: 1400 };

// ========== CSS ANIMATIONS (injected once) ==========
const animationCSS = `
@keyframes dp-fadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes dp-fadeInUp { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: translateY(0); } }
@keyframes dp-fadeInDown { from { opacity: 0; transform: translateY(-40px); } to { opacity: 1; transform: translateY(0); } }
@keyframes dp-fadeInLeft { from { opacity: 0; transform: translateX(-60px); } to { opacity: 1; transform: translateX(0); } }
@keyframes dp-fadeInRight { from { opacity: 0; transform: translateX(60px); } to { opacity: 1; transform: translateX(0); } }
@keyframes dp-zoomIn { from { opacity: 0; transform: scale(0.6); } to { opacity: 1; transform: scale(1); } }
@keyframes dp-zoomOut { from { opacity: 0; transform: scale(1.3); } to { opacity: 1; transform: scale(1); } }
@keyframes dp-bounceIn { 0% { opacity: 0; transform: scale(0.3); } 50% { opacity: 1; transform: scale(1.08); } 70% { transform: scale(0.95); } 100% { transform: scale(1); } }
@keyframes dp-flipInX { from { opacity: 0; transform: perspective(400px) rotateX(90deg); } to { opacity: 1; transform: perspective(400px) rotateX(0); } }
@keyframes dp-flipInY { from { opacity: 0; transform: perspective(400px) rotateY(90deg); } to { opacity: 1; transform: perspective(400px) rotateY(0); } }
@keyframes dp-rotateIn { from { opacity: 0; transform: rotate(-200deg) scale(0.5); } to { opacity: 1; transform: rotate(0) scale(1); } }
@keyframes dp-slideInUp { from { opacity: 0; transform: translateY(100%); } to { opacity: 1; transform: translateY(0); } }
@keyframes dp-slideInDown { from { opacity: 0; transform: translateY(-100%); } to { opacity: 1; transform: translateY(0); } }
@keyframes dp-slideInLeft { from { opacity: 0; transform: translateX(-100%); } to { opacity: 1; transform: translateX(0); } }
@keyframes dp-slideInRight { from { opacity: 0; transform: translateX(100%); } to { opacity: 1; transform: translateX(0); } }

@keyframes dp-pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
@keyframes dp-shake { 0%, 100% { transform: translateX(0); } 20% { transform: translateX(-4px); } 40% { transform: translateX(4px); } 60% { transform: translateX(-4px); } 80% { transform: translateX(4px); } }
@keyframes dp-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }

/* Scroll animation: hidden by default */
.dp-anim { opacity: 0; }
.dp-anim.dp-visible { animation-fill-mode: both; }

/* Hover effects - all use CSS variables for duration/easing + preserve border-radius */
[class*="dp-hover-"] {
  overflow: hidden;
  border-radius: inherit;
  --dp-hover-duration: 0.3s;
  --dp-hover-easing: ease;
}

.dp-hover-zoom { transition: transform var(--dp-hover-duration) var(--dp-hover-easing) !important; }
.dp-hover-zoom:hover { transform: scale(1.08) !important; }

.dp-hover-zoomSlow { transition: transform var(--dp-hover-duration) var(--dp-hover-easing) !important; }
.dp-hover-zoomSlow:hover { transform: scale(1.05) !important; }

.dp-hover-shrink { transition: transform var(--dp-hover-duration) var(--dp-hover-easing) !important; }
.dp-hover-shrink:hover { transform: scale(0.95) !important; }

.dp-hover-lift { transition: transform var(--dp-hover-duration) var(--dp-hover-easing), box-shadow var(--dp-hover-duration) var(--dp-hover-easing) !important; }
.dp-hover-lift:hover { transform: translateY(-6px) !important; box-shadow: 0 12px 30px rgba(0,0,0,0.3) !important; }

.dp-hover-glow { transition: box-shadow var(--dp-hover-duration) var(--dp-hover-easing) !important; }
.dp-hover-glow:hover { box-shadow: 0 0 20px rgba(11,180,170,0.5), 0 0 40px rgba(11,180,170,0.2) !important; }

.dp-hover-brightness { transition: filter var(--dp-hover-duration) var(--dp-hover-easing) !important; }
.dp-hover-brightness:hover { filter: brightness(1.2) !important; }

.dp-hover-darken { transition: filter var(--dp-hover-duration) var(--dp-hover-easing) !important; }
.dp-hover-darken:hover { filter: brightness(0.8) !important; }

.dp-hover-blur { transition: filter var(--dp-hover-duration) var(--dp-hover-easing) !important; }
.dp-hover-blur:hover { filter: blur(2px) !important; }

.dp-hover-borderGlow { transition: box-shadow var(--dp-hover-duration) var(--dp-hover-easing), border-color var(--dp-hover-duration) var(--dp-hover-easing) !important; border: 2px solid transparent; }
.dp-hover-borderGlow:hover { border-color: rgba(11,180,170,0.6) !important; box-shadow: 0 0 15px rgba(11,180,170,0.3) !important; }

.dp-hover-tiltLeft { transition: transform var(--dp-hover-duration) var(--dp-hover-easing) !important; }
.dp-hover-tiltLeft:hover { transform: rotate(-3deg) !important; }

.dp-hover-tiltRight { transition: transform var(--dp-hover-duration) var(--dp-hover-easing) !important; }
.dp-hover-tiltRight:hover { transform: rotate(3deg) !important; }

.dp-hover-pulse:hover { animation: dp-pulse 1s var(--dp-hover-easing) infinite !important; }

.dp-hover-shake:hover { animation: dp-shake var(--dp-hover-duration) var(--dp-hover-easing) !important; }

.dp-hover-float:hover { animation: dp-float 2s var(--dp-hover-easing) infinite !important; }

/* Image-specific hover: zoom INSIDE container (container stays same size) */
.dp-img-hover-zoom:hover { transform: scale(1.12) !important; }
.dp-img-hover-zoomSlow:hover { transform: scale(1.06) !important; }
.dp-img-hover-shrink:hover { transform: scale(0.92) !important; }
.dp-img-hover-brightness:hover { filter: brightness(1.15) !important; }
.dp-img-hover-darken:hover { filter: brightness(0.75) !important; }
.dp-img-hover-blur:hover { filter: blur(3px) !important; }
.dp-img-hover-lift:hover { transform: scale(1.05) translateY(-4px) !important; }
.dp-img-hover-glow:hover { filter: brightness(1.1) !important; transform: scale(1.03) !important; }
.dp-img-hover-tiltLeft:hover { transform: scale(1.05) rotate(-2deg) !important; }
.dp-img-hover-tiltRight:hover { transform: scale(1.05) rotate(2deg) !important; }
.dp-img-hover-borderGlow:hover { filter: brightness(1.1) !important; }
.dp-img-hover-pulse:hover { animation: dp-pulse 1.5s ease infinite !important; }
.dp-img-hover-shake:hover { animation: dp-shake 0.4s ease !important; }
.dp-img-hover-float:hover { animation: dp-float 2s ease infinite !important; }

/* Responsive: mobile columns */
@media (max-width: 768px) {
  .dp-row-mobile-1 { grid-template-columns: 1fr !important; }
  .dp-row-mobile-2 { grid-template-columns: repeat(2, 1fr) !important; }
}

/* Image Compare Slider */
.dp-compare {
  position: relative;
  overflow: hidden;
  cursor: ew-resize;
  user-select: none;
  -webkit-user-select: none;
}
.dp-compare img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
}
.dp-compare-after {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}
.dp-compare-divider {
  position: absolute;
  top: 0;
  width: 3px;
  height: 100%;
  background: #fff;
  z-index: 3;
  box-shadow: 0 0 8px rgba(0,0,0,0.5);
}
.dp-compare-handle {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 40px;
  height: 40px;
  background: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 10px rgba(0,0,0,0.4);
  z-index: 4;
  cursor: ew-resize;
}
.dp-compare-handle::before {
  content: '';
  width: 0;
  height: 0;
  border-top: 6px solid transparent;
  border-bottom: 6px solid transparent;
  border-right: 7px solid #555;
  margin-right: 4px;
}
.dp-compare-handle::after {
  content: '';
  width: 0;
  height: 0;
  border-top: 6px solid transparent;
  border-bottom: 6px solid transparent;
  border-left: 7px solid #555;
  margin-left: 4px;
}
.dp-compare-label {
  position: absolute;
  bottom: 16px;
  padding: 6px 16px;
  background: rgba(0,0,0,0.6);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  border-radius: 20px;
  z-index: 2;
  pointer-events: none;
}
.dp-compare-label-before { left: 16px; }
.dp-compare-label-after { right: 16px; }
`;

// Inject CSS once
let cssInjected = false;
function injectCSS() {
  if (cssInjected) return;
  const style = document.createElement('style');
  style.textContent = animationCSS;
  document.head.appendChild(style);
  cssInjected = true;
}

// ========== Intersection Observer Hook ==========
function useScrollAnimation() {
  const observerRef = useRef(null);

  const observe = useCallback((node) => {
    if (!node) return;
    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('dp-visible');
              const anim = entry.target.dataset.animation;
              const duration = entry.target.dataset.animationDuration || '0.6';
              const delay = entry.target.dataset.animationDelay || '0';
              if (anim && anim !== 'none') {
                entry.target.style.animation = `dp-${anim} ${duration}s ease ${delay}s both`;
              }
              observerRef.current?.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
      );
    }
    observerRef.current.observe(node);
  }, []);

  useEffect(() => {
    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  return observe;
}

// ========== AnimatedWrapper ==========
// ========== Image Compare Slider ==========
function ImageCompareSlider({ before, after, labelBefore, labelAfter, borderRadius, height, width, align }) {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const updatePos = useCallback((clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = clientX - rect.left;
    const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setPos(pct);
  }, []);

  const onPointerDown = useCallback((e) => {
    dragging.current = true;
    updatePos(e.clientX);
    e.preventDefault();
  }, [updatePos]);

  const onPointerMove = useCallback((e) => {
    if (!dragging.current) return;
    updatePos(e.clientX);
  }, [updatePos]);

  const onPointerUp = useCallback(() => {
    dragging.current = false;
  }, []);

  useEffect(() => {
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [onPointerMove, onPointerUp]);

  if (!before || !after) return null;

  // Convert % to vh for height
  const resolvedHeight = height && height.endsWith('%') ? height.replace('%', 'vh') : height;
  const containerStyle = {
    borderRadius: (borderRadius ?? 12) + 'px',
    ...(width ? { maxWidth: width, width: '100%' } : {}),
    ...(resolvedHeight ? { height: resolvedHeight, overflow: 'hidden' } : {}),
    ...(align === 'center' ? { margin: '0 auto' } : {}),
    ...(align === 'right' ? { marginLeft: 'auto' } : {}),
  };

  const imgStyle = height
    ? { width: '100%', height: '100%', objectFit: 'cover', display: 'block' }
    : { width: '100%', display: 'block' };

  return (
    <div
      ref={containerRef}
      className="dp-compare"
      onPointerDown={onPointerDown}
      style={containerStyle}
    >
      {/* Before image */}
      <img src={before} alt={labelBefore || 'Before'} style={imgStyle} />
      {/* After (clipped, absolute overlay) */}
      <div className="dp-compare-after" style={{ clipPath: `inset(0 0 0 ${pos}%)` }}>
        <img src={after} alt={labelAfter || 'After'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
      {/* Divider line */}
      <div className="dp-compare-divider" style={{ left: `${pos}%`, transform: 'translateX(-50%)' }} />
      {/* Handle */}
      <div className="dp-compare-handle" style={{ left: `${pos}%`, top: '50%' }} />
      {/* Labels */}
      {labelBefore && <span className="dp-compare-label dp-compare-label-before">{labelBefore}</span>}
      {labelAfter && <span className="dp-compare-label dp-compare-label-after">{labelAfter}</span>}
    </div>
  );
}

function AnimatedWrapper({ animation, animationDuration, animationDelay, hoverEffect, hoverDuration, hoverEasing, borderRadius, children, style, className = '' }) {
  const ref = useRef(null);
  const observe = useScrollAnimation();
  const hasScrollAnim = animation && animation !== 'none';
  const hasHover = hoverEffect && hoverEffect !== 'none';

  useEffect(() => {
    if (hasScrollAnim && ref.current) {
      observe(ref.current);
    }
  }, [hasScrollAnim, observe]);

  const classes = [
    className,
    hasScrollAnim ? 'dp-anim' : '',
    hasHover ? `dp-hover-${hoverEffect}` : '',
  ].filter(Boolean).join(' ');

  const wrapperStyle = {
    ...style,
    ...(borderRadius != null ? { borderRadius: borderRadius + 'px', overflow: 'hidden' } : {}),
    ...(hasHover ? {
      '--dp-hover-duration': (hoverDuration ?? 0.3) + 's',
      '--dp-hover-easing': hoverEasing || 'ease',
    } : {}),
  };

  return (
    <div
      ref={ref}
      className={classes}
      data-animation={animation || 'none'}
      data-animation-duration={animationDuration ?? 0.6}
      data-animation-delay={animationDelay ?? 0}
      style={wrapperStyle}
    >
      {children}
    </div>
  );
}

// ========== RenderElement ==========
function RenderElement({ el }) {
  if (!el) return null;
  const style = { textAlign: el.align || 'left' };
  const hasAnim = (el.animation && el.animation !== 'none') || (el.hoverEffect && el.hoverEffect !== 'none');

  const wrapWithAnimation = (content) => {
    if (!hasAnim) return content;
    return (
      <AnimatedWrapper
        animation={el.animation}
        animationDuration={el.animationDuration}
        animationDelay={el.animationDelay}
        hoverEffect={el.hoverEffect}
        hoverDuration={el.hoverDuration}
        hoverEasing={el.hoverEasing}
        borderRadius={el.borderRadius}
      >
        {content}
      </AnimatedWrapper>
    );
  };

  switch (el.type) {
    case 'heading': {
      const Tag = el.headingLevel || 'h2';
      return wrapWithAnimation(
        React.createElement(Tag, {
          style: {
            ...style,
            fontSize: el.fontSize ? `${el.fontSize}px` : '28px',
            color: el.color || 'inherit',
            fontWeight: el.bold !== false ? 700 : 400,
            margin: 0,
            marginBottom: el.marginBottom ?? 12,
            textTransform: el.textTransform || 'none',
          }
        }, el.content)
      );
    }
    case 'text': {
      const textStyle = {
        ...style,
        lineHeight: el.lineHeight ?? 1.8,
        color: el.color || 'inherit',
        textTransform: el.textTransform || 'none',
        fontSize: el.fontSize ? `${el.fontSize}px` : '16px',
        fontWeight: el.bold ? 700 : 400,
      };
      const textContent = <div style={textStyle} dangerouslySetInnerHTML={{ __html: el.content }} />;
      if (el.linkUrl) {
        return wrapWithAnimation(
          <a href={el.linkUrl} target={el.linkTarget || '_self'} rel={el.linkTarget === '_blank' ? 'noopener noreferrer' : undefined} style={{ textDecoration: 'none', color: 'inherit' }}>{textContent}</a>
        );
      }
      return wrapWithAnimation(textContent);
    }
    case 'image': {
      if (!el.src) return null;
      const radius = (el.borderRadius ?? 12) + 'px';
      const imgHover = el.hoverEffect && el.hoverEffect !== 'none';
      const imgHoverClass = imgHover ? `dp-img-hover-${el.hoverEffect}` : '';
      const imgStyle = {
        width: '100%',
        height: '100%',
        objectFit: el.objectFit || 'cover',
        display: 'block',
        transition: imgHover ? `transform ${el.hoverDuration ?? 0.3}s ${el.hoverEasing || 'ease'}, filter ${el.hoverDuration ?? 0.3}s ${el.hoverEasing || 'ease'}` : undefined,
      };
      const containerStyle = {
        maxWidth: el.maxWidth || '100%',
        borderRadius: radius,
        overflow: 'hidden',
        boxShadow: el.shadow ? '0 4px 20px rgba(0,0,0,0.3)' : 'none',
        ...(el.align === 'center' ? { margin: '0 auto' } : {}),
        ...(el.align === 'right' ? { marginLeft: 'auto' } : {}),
      };
      const imgEl = <img src={el.src} alt={el.altText || ''} style={imgStyle} className={imgHoverClass} />;
      const wrapped = el.linkUrl ? (
        <a href={el.linkUrl} target={el.linkTarget || '_self'} rel={el.linkTarget === '_blank' ? 'noopener noreferrer' : undefined} style={{ display: 'block' }}>
          {imgEl}
        </a>
      ) : imgEl;
      // For images: scroll animation on wrapper, but hover on img itself (not wrapper)
      const elWithoutHover = { ...el, hoverEffect: 'none' };
      const hasScrollAnim = el.animation && el.animation !== 'none';
      if (hasScrollAnim) {
        return (
          <AnimatedWrapper animation={el.animation} animationDuration={el.animationDuration} animationDelay={el.animationDelay} hoverEffect="none" borderRadius={el.borderRadius}>
            <div style={containerStyle}>{wrapped}</div>
          </AnimatedWrapper>
        );
      }
      return <div style={containerStyle}>{wrapped}</div>;
    }
    case 'video': {
      if (!el.src) return null;
      const videoH = el.elHeight ? (el.elHeight.endsWith && el.elHeight.endsWith('%') ? el.elHeight.replace('%', 'vh') : el.elHeight) : undefined;
      const videoStyle = { width: '100%', borderRadius: 12, ...(videoH ? { height: videoH, objectFit: 'cover' } : { aspectRatio: '16/9' }) };
      if (el.src.includes('youtube') || el.src.includes('youtu.be')) {
        return wrapWithAnimation(<iframe src={el.src.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')} style={{ ...videoStyle, border: 'none' }} allowFullScreen title="Video" />);
      }
      return wrapWithAnimation(<video src={el.src} controls style={videoStyle} />);
    }
    case 'button': {
      const pad = el.size === 'sm' ? '8px 20px' : el.size === 'lg' ? '16px 44px' : '12px 32px';
      const bg = el.outlined ? 'transparent' : (el.bgColor || 'var(--color-primary)');
      const border = el.outlined ? `2px solid ${el.bgColor || 'var(--color-primary)'}` : 'none';
      const color = el.outlined ? (el.bgColor || 'var(--color-primary)') : (el.textColor || '#fff');
      const fontSize = el.size === 'lg' ? 18 : el.size === 'sm' ? 13 : 15;
      return wrapWithAnimation(
        <div style={style}>
          <a
            href={el.buttonLink || '#'}
            target={el.buttonTarget || '_self'}
            rel={el.buttonTarget === '_blank' ? 'noopener noreferrer' : undefined}
            style={{
              display: el.btnFullWidth ? 'block' : 'inline-block',
              padding: pad,
              background: bg,
              color,
              border,
              borderRadius: (el.btnBorderRadius ?? 30) + 'px',
              textDecoration: 'none',
              fontWeight: 600,
              textAlign: 'center',
              fontSize,
              transition: 'opacity 0.2s',
            }}
          >
            {el.buttonText || 'Click'}
          </a>
        </div>
      );
    }
    case 'spacer':
      return <div style={{ height: el.content ? `${el.content}px` : '40px' }} />;
    case 'divider':
      return wrapWithAnimation(
        <div style={{
          margin: `${el.marginTop ?? 20}px auto ${el.marginBottom ?? el.marginTop ?? 20}px`,
          width: el.dividerWidth || '100%',
          height: el.thickness ?? 1,
          background: el.dividerColor || '#333',
        }} />
      );
    case 'gallery':
      return wrapWithAnimation(
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${el.gridColumns || 3}, 1fr)`,
          gap: (el.galleryGap ?? 8) + 'px',
        }}>
          {(el.images || []).map((img, i) => (
            <img key={i} src={img} alt="" style={{
              width: '100%',
              aspectRatio: '1',
              objectFit: 'cover',
              borderRadius: (el.galleryBorderRadius ?? 8) + 'px',
            }} />
          ))}
        </div>
      );
    case 'compare':
      return wrapWithAnimation(
        <ImageCompareSlider
          before={el.compareBefore}
          after={el.compareAfter}
          labelBefore={el.labelBefore}
          labelAfter={el.labelAfter}
          borderRadius={el.borderRadius}
          height={el.elHeight}
          width={el.elWidth}
          align={el.align}
        />
      );
    case 'html':
      return wrapWithAnimation(<div dangerouslySetInnerHTML={{ __html: el.content }} />);
    default:
      return null;
  }
}

// ========== RenderRow ==========
function RenderRow({ row, isFullWidthPage }) {
  const hasBgImage = !!row.bgImage;
  const hasBgColor = !!row.bgColor;
  const isFullWidth = row.fullWidth || isFullWidthPage;
  const cw = containerWidthMap[row.containerWidth || 'default'];
  const verticalAlignMap = { top: 'flex-start', center: 'center', bottom: 'flex-end' };

  const hasRowAnim = row.animation && row.animation !== 'none';

  const outerStyle = {
    position: 'relative',
    overflow: 'hidden',
    ...(isFullWidth ? { width: '100vw', marginLeft: 'calc(-50vw + 50%)' } : {}),
    ...(hasBgImage ? {
      backgroundImage: `url(${row.bgImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    } : {}),
    ...(hasBgColor && !hasBgImage ? { background: row.bgColor } : {}),
  };

  const innerStyle = {
    position: 'relative',
    maxWidth: row.containerWidth === 'full' ? '100%' : (cw || 1200),
    margin: '0 auto',
    padding: `${row.paddingTop ?? 0}px 24px ${row.paddingBottom ?? 0}px`,
    display: 'grid',
    gridTemplateColumns: `repeat(${row.columns || 1}, 1fr)`,
    gap: (row.gap ?? 24) + 'px',
    alignItems: verticalAlignMap[row.verticalAlign || 'center'] || 'center',
    textAlign: row.textAlign || 'left',
  };

  const mobileClass = `dp-row-mobile-${row.mobileColumns ?? 1}`;

  const content = (
    <div style={outerStyle}>
      {hasBgImage && (
        <div style={{
          position: 'absolute',
          inset: 0,
          background: row.bgOverlayColor || '#000',
          opacity: row.bgOverlayOpacity ?? 0.5,
          zIndex: 0,
        }} />
      )}
      <div style={innerStyle} className={mobileClass}>
        {(row.elements || []).slice(0, row.columns || 1).map((el, ei) => {
          const cellStyle = { position: 'relative', zIndex: 1 };
          if (el.elWidth) {
            cellStyle.width = el.elWidth;
            if ((el.align || row.textAlign) === 'center') cellStyle.margin = '0 auto';
            if ((el.align || row.textAlign) === 'right') cellStyle.marginLeft = 'auto';
          }
          if (el.elHeight) {
            const h = el.elHeight;
            cellStyle.height = h.endsWith && h.endsWith('%') ? h.replace('%', 'vh') : h;
            cellStyle.overflow = 'hidden';
          }
          if (el.elPadding) cellStyle.padding = el.elPadding;
          if (el.elMarginTop) cellStyle.marginTop = el.elMarginTop;
          if (el.elMarginBottom) cellStyle.marginBottom = el.elMarginBottom;
          if (el.elOpacity != null && el.elOpacity !== 1) cellStyle.opacity = el.elOpacity;
          if (el.elBgColor) cellStyle.background = el.elBgColor;
          if (el.elBorderWidth) {
            cellStyle.border = `${el.elBorderWidth}px ${el.elBorderStyle || 'solid'} ${el.elBorderColor || '#fff'}`;
          }
          if (el.elBorderRadius) cellStyle.borderRadius = el.elBorderRadius;
          return (
            <div key={ei} style={cellStyle}>
              <RenderElement el={el} />
            </div>
          );
        })}
      </div>
    </div>
  );

  if (hasRowAnim) {
    return (
      <AnimatedWrapper
        animation={row.animation}
        animationDuration={row.animationDuration}
        animationDelay={row.animationDelay}
      >
        {content}
      </AnimatedWrapper>
    );
  }

  return content;
}

// ========== DynamicPage ==========
function DynamicPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    injectCSS();
    window.scrollTo(0, 0);
    fetch(`/api/dynamic-pages/${slug}`)
      .then(r => r.json())
      .then(d => { if (d.success) setPage(d.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>Loading...</div>;
  if (!page) return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#888', gap: 16 }}>
      <p>Page not found</p>
      <button onClick={() => navigate('/')} style={{ padding: '10px 24px', background: 'var(--color-primary)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}>Back to Home</button>
    </div>
  );

  const bgStyle = {};
  if (page.backgroundColor) bgStyle.background = page.backgroundColor;
  if (page.textColor) bgStyle.color = page.textColor;
  const isFullWidthPage = page.layout === 'full-width';

  return (
    <>
      <main style={{ paddingTop: page.hideTitle && page.hideBackButton ? 72 : 90, minHeight: '100vh', background: 'var(--color-bg-dark)', ...bgStyle, overflow: 'hidden' }}>
        <div className={isFullWidthPage ? '' : 'container'}>
          {!page.hideBackButton && (
            <button onClick={() => navigate(-1)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, cursor: 'pointer', fontSize: 14, marginBottom: 24 }}>
              &larr; Back
            </button>
          )}

          {page.heroImage && (
            <div style={{ width: '100%', height: page.heroHeight || 300, borderRadius: page.hideTitle ? 0 : 12, overflow: 'hidden', marginBottom: page.hideTitle ? 0 : 32, ...(isFullWidthPage ? { width: '100vw', marginLeft: 'calc(-50vw + 50%)', borderRadius: 0 } : {}) }}>
              <img src={page.heroImage} alt={page.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          )}

          {!page.hideTitle && (
            <>
              <h1 style={{ fontSize: '2.5rem', fontWeight: 800, color: '#fff', marginBottom: 8 }}>{page.title}</h1>
              {page.description && <p style={{ color: 'var(--color-text-muted)', marginBottom: 32, fontSize: '0.95rem' }}>{page.description}</p>}
            </>
          )}

          {/* Builder Rows - show when mode is 'builder' or 'both' or not set */}
          {(page.contentMode !== 'html') && page.rows && page.rows.length > 0 && (
            <div style={{ marginBottom: 60, paddingBottom: 20 }}>
              {page.rows.map((row, ri) => (
                <RenderRow key={ri} row={row} isFullWidthPage={isFullWidthPage} />
              ))}
            </div>
          )}

          {/* HTML Content - show when mode is 'html' or 'both' */}
          {(page.contentMode === 'html' || page.contentMode === 'both') && page.content && (
            <div style={{ lineHeight: 1.8, color: 'var(--color-text-muted)', marginBottom: 60 }} dangerouslySetInnerHTML={{ __html: page.content }} />
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}

export default DynamicPage;
